import { forwardRef, useState } from 'react';

import { maskitoDateOptionsGenerator } from '@maskito/kit';
import { useMaskito } from '@maskito/react';
import { CalendarIcon } from 'lucide-react';

import { useMediaQuery } from '../../hooks/use-media-query.jsx';
import { cn } from '../../utils/cn.js';

import { Button } from './button.jsx';
import { Calendar } from './calendar.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog.jsx';
import { Drawer, DrawerContent, DrawerTrigger } from './drawer.jsx';
import { Input } from './input.jsx';

const dateOptions = maskitoDateOptionsGenerator({
  mode: 'dd/mm/yyyy',
  separator: '-',
});

const DatePickerUI = ({ children, input, variant = 'outline' }) => {
  const maskedInputRef = useMaskito({ options: dateOptions });

  const variants = {
    portal: 'border-portal-foreground h-[46px] rounded-2xl',
  };

  return (
    <div className="relative w-full">
      <Input
        {...input}
        className={cn(
          'w-full justify-start text-left font-normal disabled:opacity-75',
          variants[variant],
          input?.className,
        )}
        onInput={input?.onChange}
        ref={maskedInputRef}
        placeholder="dd-MM-yyyy"
      />
      {children}
    </div>
  );
};

export function DateInputWithDatePicker({
  input,
  calendar,
  variant = 'outline',
}) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [open, setOpen] = useState(false);

  const { onSelect: originalOnSelect, ...restCalendarProps } = calendar || {};

  const handleDateSelect = (date) => {
    calendar?.onSelect(date);
    setOpen(false);
  };

  const TriggerButton = forwardRef((props, ref) => {
    return (
      <Button
        {...props}
        type="button"
        variant="ghost"
        ref={ref}
        className={cn(
          'absolute right-3 top-[50%] h-auto translate-y-[-50%] rounded-lg p-1.5',
        )}
      >
        <CalendarIcon className="size-4" />
      </Button>
    );
  });
  TriggerButton.displayName = 'TriggerButton';

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DatePickerUI input={input} variant={variant}>
          <DialogTrigger asChild>
            <TriggerButton />
          </DialogTrigger>
        </DatePickerUI>
        <DialogContent className="max-w-96">
          <DialogHeader>
            <DialogTitle>Calendário</DialogTitle>
          </DialogHeader>
          <div className="flex w-auto flex-row justify-center p-0">
            <Calendar
              {...restCalendarProps}
              onSelect={(date) => handleDateSelect(date)}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DatePickerUI input={input} variant={variant}>
        <DrawerTrigger asChild>
          <TriggerButton />
        </DrawerTrigger>
      </DatePickerUI>
      <DrawerContent>
        <div className="flex w-auto flex-row justify-center p-0">
          <Calendar {...restCalendarProps} onSelect={handleDateSelect} />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
